import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

function Particles({ count = 4200, color = "#00FF66", size = 0.02, radius = 5.5 }) {
  const ref = useRef();

  const [positions, geometry] = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 3 + Math.random() * radius;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.55;
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(arr, 3));
    return [arr, geo];
  }, [count, radius]);

  useFrame((state, delta) => {
    if (!ref.current) return;
    ref.current.rotation.y += delta * 0.05;
    ref.current.rotation.x += delta * 0.02;
  });

  return (
    <points ref={ref} geometry={geometry}>
      <pointsMaterial
        color={color}
        size={size}
        sizeAttenuation
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function CameraRig() {
  useFrame((state) => {
    const scrollY = typeof window !== "undefined" ? window.scrollY : 0;
    const doc = typeof document !== "undefined" ? document.documentElement : { scrollHeight: 1, clientHeight: 1 };
    const max = doc.scrollHeight - doc.clientHeight;
    const p = max > 0 ? scrollY / max : 0;
    const t = state.clock.elapsedTime;

    state.camera.position.z = 8 - p * 6;
    state.camera.position.x = Math.sin(t * 0.15) * 0.4;
    state.camera.position.y = Math.cos(t * 0.1) * 0.2 - p * 0.5;
    state.camera.lookAt(0, 0, 0);
  });
  return null;
}

function GridPlane() {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const scrollY = typeof window !== "undefined" ? window.scrollY : 0;
    const doc = typeof document !== "undefined" ? document.documentElement : { scrollHeight: 1, clientHeight: 1 };
    const max = doc.scrollHeight - doc.clientHeight;
    const p = max > 0 ? scrollY / max : 0;
    ref.current.position.z = -2 + p * 4;
    ref.current.rotation.z = state.clock.elapsedTime * 0.05;
  });
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2.2, 0, 0]} position={[0, -1.6, -2]}>
      <planeGeometry args={[30, 30, 40, 40]} />
      <meshBasicMaterial color="#00FF66" wireframe transparent opacity={0.08} />
    </mesh>
  );
}

function TorusKnot() {
  const ref = useRef();
  useFrame((state, delta) => {
    if (!ref.current) return;
    ref.current.rotation.x += delta * 0.15;
    ref.current.rotation.y += delta * 0.2;
  });
  return (
    <mesh ref={ref} position={[0, 0, 0]}>
      <torusKnotGeometry args={[1.2, 0.32, 200, 24]} />
      <meshBasicMaterial color="#00F0FF" wireframe transparent opacity={0.35} />
    </mesh>
  );
}

export default function ParticleField() {
  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (prefersReduced) {
    return (
      <div
        data-testid="hero-3d-canvas"
        className="absolute inset-0 -z-0 bg-[#050505] overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 40%, rgba(0,255,102,0.18), transparent 55%), radial-gradient(circle at 70% 70%, rgba(0,240,255,0.12), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(0,255,102,0.15) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(0,255,102,0.15) 0 1px, transparent 1px 40px)",
          }}
        />
      </div>
    );
  }

  return (
    <div data-testid="hero-3d-canvas" className="absolute inset-0 -z-0" aria-hidden="true">
      <Canvas
        camera={{ position: [0, 0, 8], fov: 60 }}
        gl={{ antialias: true, alpha: true }}
        dpr={[1, 1.5]}
      >
        <color attach="background" args={["#050505"]} />
        <fog attach="fog" args={["#050505", 4, 14]} />
        <ambientLight intensity={0.4} />
        <TorusKnot />
        <Particles count={3500} color="#00FF66" size={0.02} radius={5.5} />
        <Particles count={1800} color="#00F0FF" size={0.012} radius={4} />
        <GridPlane />
        <CameraRig />
      </Canvas>
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
    </div>
  );
}
